import { Info, AlertTriangle, XCircle, CheckCircle, Plus } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { IdentifiedProperty } from './IdentificationPropertyRow'
import type { IdRule } from './IdRuleComplianceBar'

interface IdSmartGuidanceBannerProps {
  properties: IdentifiedProperty[]
  idRule: IdRule
  relinquishedFMV: number | null
}

type BannerVariant = 'info' | 'warning' | 'error' | 'success'

interface BannerConfig {
  variant: BannerVariant
  message: string
}

function formatDollars(value: number): string {
  return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

function resolveBanner(
  properties: IdentifiedProperty[],
  idRule: IdRule,
  relinquishedFMV: number | null
): BannerConfig | null {
  const totalFMV = properties.reduce((sum, p) => sum + (p.estimatedFMV ?? 0), 0)
  const propertyCount = properties.filter((p) => p.address.trim()).length
  const baseFMV = relinquishedFMV ?? 0

  if (propertyCount === 0) {
    const target = baseFMV > 0 ? ` Your relinquished sale price is ${formatDollars(baseFMV)} — aim to allocate at least that much.` : ''
    return {
      variant: 'info',
      message: `Enter a like-kind property or add DSTs from the Marketplace to begin.${target}`,
    }
  }

  if (idRule === '3-property') {
    if (propertyCount > 3) {
      return {
        variant: 'error',
        message: `You have ${propertyCount} properties identified. The 3-property rule only allows 3. Remove a property or switch to the 200% rule.`,
      }
    }
    if (propertyCount === 3) {
      return {
        variant: 'warning',
        message: "You've identified 3 properties (max under the 3-property rule). Switch to the 200% rule to identify more.",
      }
    }
    return null
  }

  if (idRule === '200-percent') {
    if (baseFMV <= 0) {
      return {
        variant: 'info',
        message: 'Set the relinquished sale price in the Sales Hub to see your 200% FMV cap.',
      }
    }
    const cap = baseFMV * 2
    const ratio = totalFMV / cap
    if (totalFMV > cap) {
      const excess = totalFMV - cap
      return {
        variant: 'error',
        message: `Total exceeds 200% of relinquished FMV by ${formatDollars(excess)}. Remove a property or lower allocations.`,
      }
    }
    if (ratio >= 0.8) {
      return {
        variant: 'warning',
        message: `Allocations are at ${Math.round(ratio * 100)}% of the 200% cap. Adding more may exceed the rule.`,
      }
    }
    if (totalFMV < baseFMV) {
      const remaining = cap - totalFMV
      return {
        variant: 'info',
        message: `${formatDollars(totalFMV)} of ${formatDollars(cap)} cap used — you have ${formatDollars(remaining)} remaining capacity. Add another DST?`,
      }
    }
    return {
      variant: 'success',
      message: `${formatDollars(totalFMV)} identified and within the ${formatDollars(cap)} cap.`,
    }
  }

  if (idRule === '95-percent') {
    const required = Math.ceil(totalFMV * 0.95)
    return {
      variant: 'info',
      message: totalFMV > 0
        ? `Under the 95% rule you must acquire at least ${formatDollars(required)} (95% of total identified ${formatDollars(totalFMV)}).`
        : 'Under the 95% rule you must acquire at least 95% of total identified FMV. Add properties to see the target.',
    }
  }

  return null
}

const VARIANT_CONTAINER: Record<BannerVariant, string> = {
  info: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20',
  warning: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
  error: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
  success: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20',
}

const VARIANT_TEXT: Record<BannerVariant, string> = {
  info: 'text-blue-800 dark:text-blue-200',
  warning: 'text-amber-800 dark:text-amber-200',
  error: 'text-red-800 dark:text-red-200',
  success: 'text-green-800 dark:text-green-200',
}

const VARIANT_ICON: Record<BannerVariant, string> = {
  info: 'text-blue-600 dark:text-blue-400',
  warning: 'text-amber-600 dark:text-amber-400',
  error: 'text-red-600 dark:text-red-400',
  success: 'text-green-600 dark:text-green-400',
}

const VARIANT_ICON_COMPONENT: Record<BannerVariant, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
  success: CheckCircle,
}

const BANNER_ARIA_ROLE: Record<BannerVariant, string> = {
  info: 'status',
  warning: 'alert',
  error: 'alert',
  success: 'status',
}

export function IdSmartGuidanceBanner({
  properties,
  idRule,
  relinquishedFMV,
}: IdSmartGuidanceBannerProps) {
  const banner = resolveBanner(properties, idRule, relinquishedFMV)
  if (!banner) return null

  const { variant, message } = banner
  const BannerIcon = VARIANT_ICON_COMPONENT[variant]
  const role = BANNER_ARIA_ROLE[variant]

  return (
    <div
      className={cn('flex items-start gap-2 rounded-lg border px-3 py-2.5', VARIANT_CONTAINER[variant])}
      role={role}
      aria-live="polite"
      aria-label="Identification guidance"
    >
      <BannerIcon
        className={cn('h-4 w-4 flex-shrink-0 mt-0.5', VARIANT_ICON[variant])}
        aria-hidden="true"
      />
      <p className={cn('text-xs', VARIANT_TEXT[variant])}>
        {message}
        {variant === 'info' && message.includes('Add another DST') && (
          <span className="inline-flex items-center gap-0.5 ml-1">
            <Plus className="h-3 w-3 inline" aria-hidden="true" />
          </span>
        )}
      </p>
    </div>
  )
}

export { resolveBanner }
